<template>
  <!--
    Скелетон загрузки таблицы аналитики. Имитирует по скруглениям и размерам:
      - верхний тулбар (как реальная панель над таблицей: слева контролы, по центру
        пагинация-точки + полоса, справа кнопки) — без рамки, отдельно;
      - саму таблицу в рамке: ОДНА шапка и строки, тянущиеся до низа.
    Показываем вместо центрального спиннера, пока данные элемента грузятся.
  -->
  <div class="qts" aria-hidden="true">
    <!-- верхний тулбар над таблицей -->
    <div class="qts__toolbar">
      <div class="qts__tgroup">
        <span class="qts__bar qts__bar--tool" style="width: 110px" />
        <span class="qts__dot qts__dot--sm qts__dot--first" />
        <span class="qts__dot qts__dot--sm" />
        <span class="qts__dot qts__dot--sm" />
        <span class="qts__bar qts__bar--tool" style="width: 150px" />
      </div>
      <div class="qts__tgroup">
        <span class="qts__bar qts__bar--tool" style="width: 120px" />
        <span class="qts__bar qts__bar--tool" style="width: 90px" />
        <span class="qts__bar qts__bar--tool" style="width: 110px" />
      </div>
    </div>

    <!-- таблица в рамке: одна шапка + строки -->
    <div class="qts__table">
      <!-- шапка: круг в первой колонке + одна длинная полоса на остальную ширину -->
      <div class="qts__row qts__row--head">
        <div class="qts__cell qts__cell--lead">
          <span class="qts__dot" />
        </div>
        <span class="qts__bar qts__bar--head" />
      </div>

      <div class="qts__divider" />

      <!-- строки: тянутся до низа, лишнее обрезается overflow: hidden -->
      <div ref="bodyEl" class="qts__body">
        <div
          v-for="r in rowCount"
          :key="`r${r}`"
          class="qts__row"
        >
          <div class="qts__cell qts__cell--lead">
            <span class="qts__dot" />
          </div>
          <div
            v-for="c in columns"
            :key="`r${r}c${c}`"
            class="qts__cell"
          >
            <span
              class="qts__bar"
              :style="{ width: cellWidth(r - 1, c - 1) }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";

const props = withDefaults(defineProps<{
  rows?: number;
  columns?: number;
}>(), {
  rows: 12,
  columns: 5,
});

// Набор ширин полос (в %), чередуем по строкам, чтобы скелетон выглядел «живым»,
// а не из одинаковых ячеек. Первая колонка обычно шире (имена), дальше уже.
const widthPatterns: number[][] = [
  [92, 70, 55, 78, 60],
  [55, 70, 48, 40, 50],
  [88, 72, 60, 80, 46],
  [70, 50, 55, 70, 64],
  [40, 45, 40, 55, 58],
  [86, 74, 62, 76, 50],
  [33, 70, 36, 60, 44],
  [82, 72, 58, 78, 62],
  [66, 60, 52, 50, 70],
  [84, 70, 60, 74, 48],
  [50, 44, 38, 64, 56],
];

function cellWidth(rowIndex: number, colIndex: number): string {
  const pattern = widthPatterns[rowIndex % widthPatterns.length];
  const w = pattern[colIndex % pattern.length];
  return `${w}%`;
}

// Высоту строки знаем из стилей (паддинги + высота полосы). По ней считаем, сколько
// строк нужно, чтобы заполнить тело до низа; следим за изменением высоты.
const ROW_HEIGHT = 50;
const bodyEl = ref<HTMLElement | null>(null);
const rowCount = ref<number>(props.rows);
let ro: ResizeObserver | null = null;

function recompute() {
  const h = bodyEl.value?.clientHeight || 0;
  const fit = Math.ceil(h / ROW_HEIGHT) + 1;
  const next = Math.max(props.rows, fit);
  if (next !== rowCount.value) rowCount.value = next;
}

onMounted(() => {
  recompute();
  if (bodyEl.value && typeof ResizeObserver !== "undefined") {
    ro = new ResizeObserver(() => recompute());
    ro.observe(bodyEl.value);
  }
});

onBeforeUnmount(() => {
  ro?.disconnect();
  ro = null;
});
</script>

<style lang="scss" scoped>
/* Селекторы плоские и простые: правило стабильности scoped SCSS в проекте. */
.qts {
  width: 100%;
  height: 100%;
  min-height: 320px;
  display: flex;
  flex-direction: column;
}

/* Верхний тулбар. По высоте и отступам совпадает с реальной панелью над таблицей:
   вертикальный отступ q-py-sm (8px) + высота контролов ~44px, по краям отступа нет
   (панель начинается у левого края, как и таблица), горизонтальные поля даёт q-page. */
.qts__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  /* сверху до элементов 16, снизу до таблицы 20 */
  padding: 16px 0 20px;
  flex: 0 0 auto;
}

.qts__tgroup {
  display: flex;
  align-items: center;
  /* между тремя кругами 12 (плюс gap до полос по краям) */
  gap: 12px;
}

/* Отступ перед первым из трёх кругов 40px. gap группы уже даёт 12, добавляем 28. */
.qts__dot--first {
  margin-left: 28px;
}

/* Таблица в рамке со скруглением. */
.qts__table {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #ededed;
  border-radius: 4px;
  overflow: hidden;
}

.qts__divider {
  height: 1px;
  background: #eee;
  flex: 0 0 auto;
}

/* Тело тянется на весь остаток высоты, лишние строки обрезаем. */
.qts__body {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}

.qts__row {
  display: flex;
  align-items: center;
  gap: 12px;
  /* высота строки 50; отступы от границ таблицы до элементов 16 по бокам */
  height: 50px;
  padding: 0 16px;
}

.qts__row--head {
  /* в шапке высота по контенту, сверху и снизу до элементов по 24, по бокам 16 */
  height: auto;
  padding: 24px 16px;
  flex: 0 0 auto;
}

.qts__cell {
  display: flex;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
}

/* Первая колонка под кружок-галку: прижата к левому отступу таблицы (16px). */
.qts__cell--lead {
  flex: 0 0 auto;
  justify-content: flex-start;
}

/* Элементы таблицы-скелетона: цвет #F2F2F2, скругление 8, высота 18. */
/* Кружок-чекбокс в первой колонке: круг 20x20, радиус 20. */
.qts__dot {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  aspect-ratio: 1 / 1;
  border-radius: 20px;
  background: #f2f2f2;
  animation: qts-pulse 1.4s ease-in-out infinite;
}

/* Точки пагинации в тулбаре: круглые 20x20, радиус 20, цвет панели #D2D2D2. */
.qts__dot--sm {
  width: 20px;
  height: 20px;
  border-radius: 20px;
  background: #d2d2d2;
}

.qts__bar {
  height: 18px;
  border-radius: 8px;
  width: 70%;
  background: #f2f2f2;
  animation: qts-pulse 1.4s ease-in-out infinite;
}

/* Полосы в тулбаре: цвет панели #D2D2D2, высота 20, ширина задаётся инлайном. */
.qts__bar--tool {
  height: 20px;
  width: auto;
  flex: 0 0 auto;
  background: #d2d2d2;
}

/* Полоса шапки: длинная, на всю оставшуюся ширину после круга, высота 20. */
.qts__bar--head {
  flex: 1 1 auto;
  width: auto;
  height: 20px;
}

@keyframes qts-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}
</style>
